import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { omniFilterPreferences } from '@stores/OmniFilterPreferences';
import { trackingStore } from '@stores/Tracking';
import { useCallback } from 'react';
import { debounce } from 'throttle-debounce';

/**
 * The media omnifilter's state and tracking. The query lives on `folder._omniFilter`, which both
 * the grid and table views read. Tokens filter media by title substring, exact YouTube ID, or
 * exact media id (see `pages/Library/utils/omniFilter.ts`), and folders by name. Filtering is live.
 *
 * Typed into the library's search line (`LibraryToolbar`), which replaced the Filters pill.
 */
export function useLibraryOmniFilter() {
  const folder = useLibraryFolder();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const trackFilterDebounced = useCallback(
    debounce(500, (query: string | null) => {
      trackingStore.libraryFilterApplied({ query, context: 'media' });
    }),
    [],
  );

  const onChange = useCallback(
    (next: string) => {
      folder.setOmniFilter(next || null);
      trackFilterDebounced(next.trim() || null);
    },
    [folder, trackFilterDebounced],
  );

  const onClear = useCallback(() => folder.setOmniFilter(null), [folder]);

  const onMatchAllChange = useCallback((matchAll: boolean) => {
    omniFilterPreferences.setMatchAll(matchAll);
    trackingStore.libraryFilterMatchModeChanged({ matchAll, context: 'media' });
  }, []);

  const onOpen = useCallback((via: 'keyboard' | 'click') => {
    trackingStore.libraryFilterOpened({ context: 'media', via });
  }, []);

  return {
    applied: folder._omniFilter ?? '',
    onChange,
    onClear,
    matchAll: omniFilterPreferences.matchAll,
    onMatchAllChange,
    onOpen,
  };
}
